import { useState } from "react"
import { Button } from "@/components/ui/Button"
import { Input } from "@/components/ui/Input"
import { currencyDisplay, formatMoneyFull, parseMoney } from "@/lib/money"
import type { ListSettings as Settings } from "../hooks/useListSettings"

interface Props {
  value: Settings
  defaults: Settings
  docRange: { min: number; max: number }
  currency: string
  onApply: (settings: Settings) => void
}

// The quick settings at the top of the page: which products make the list.
// Each person's own, kept in their browser; nobody else's list changes.
export default function ListSettings({ value, defaults, docRange, currency, onApply }: Props) {
  const [doc, setDoc] = useState(String(value.doc))
  const [min, setMin] = useState(formatMoneyFull(value.min, currency))

  const docDays = Number(doc)
  const minValue = parseMoney(min, currency)
  const valid = Number.isInteger(docDays) && docDays >= docRange.min && docDays <= docRange.max && minValue !== null && minValue >= 0
  const changed = valid && (docDays !== value.doc || minValue !== value.min)
  const isDefault = value.doc === defaults.doc && value.min === defaults.min

  const apply = () => changed && onApply({ doc: docDays, min: minValue as number })
  const onEnter = (e: React.KeyboardEvent) => e.key === "Enter" && apply()

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border bg-card px-3 py-2 text-sm">
      <span>Show products with DOC of</span>
      <Input
        type="number"
        min={docRange.min}
        max={docRange.max}
        value={doc}
        onChange={(e) => setDoc(e.target.value)}
        onKeyDown={onEnter}
        className="h-8 w-20 text-right tabular-nums"
        aria-label="DOC cut-off in days"
      />
      <span>days or more, and excess value of</span>
      <Input
        value={min}
        onChange={(e) => setMin(e.target.value)}
        onBlur={() => minValue !== null && setMin(formatMoneyFull(minValue, currency))}
        onKeyDown={onEnter}
        className="h-8 w-32 text-right tabular-nums"
        aria-label="Minimum excess value"
      />
      <span>{currencyDisplay(currency).symbol} or more</span>
      {changed && <Button size="sm" onClick={apply}>Apply</Button>}
      {!changed && !isDefault && (
        <Button size="sm" variant="ghost" onClick={() => onApply(defaults)}>
          Reset to {defaults.doc} days, {formatMoneyFull(defaults.min, currency)} {currencyDisplay(currency).symbol}
        </Button>
      )}
    </div>
  )
}
